<script lang="ts" setup>
import { SelectionModeEnum } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { SearchMode } from "~/core/composables/useSearch";
import { useToggle } from "~/core/composables/useToggle";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import {
  NotificationKey,
  showNotificationWarning,
} from "~/core/utils/notifications";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useAttachDetachEntitiesToSector } from "~/modules/sector/composables/useAttachDetachEntitiesToSector";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SectorAttachDetachPayload } from "~/modules/sector/models/SectorAttachDetachPayload.interface";
import type { SelectedItemType } from "~/modules/sector/models/SelectedItem.type";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { displayActionStatus } from "~/modules/sector/utils/displayActionsStatus";
import { getSearchComposable } from "~/modules/sector/utils/getSearchComposable";
import { Filters, SectorResource } from "~/types";
import ILxcPlus from "~icons/lxc/plus";

const props = defineProps<{
  primaryKey: string;
  sector: Sector;
  resource: SectorResource;
}>();
const emit = defineEmits(["change"]);

const { sectors } = storeToRefs(useSectorStore());

const { t } = useI18n();
const [attachModalVisible, toggleAttachModalVisible] = useToggle();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const {
  error: errorAttachDetach,
  results: resultsAttachDetach,
  attachDetachEntities,
  isLoading: isLoadingAttachDetach,
} = useAttachDetachEntitiesToSector(currentOrganization.value?.code);

const useSearch = getSearchComposable(props.resource);
const {
  itemType,
  isLoading,
  results,
  error,
  getDescription,
  filters,
  fetchData,
  setFilter,
  canManageItems,
  getItemIds,
} = useSearch!({
  organizationCode: currentOrganization.value?.code,
  sectorCode: props.sector.code,
  outSector: true,
  searchMode: SearchMode.FILTER_SEARCH,
});

const attachSelectedItems = ref<SelectedItemType[]>([]);
const displayActions = computed(() => canManageItems(props.sector));

const filterKey = computed(() => {
  switch (props.resource) {
    case SectorResource.DTWINS:
      return Filters.DTWIN_NAME_OR_SERIAL_NUMBER;
    case SectorResource.USER:
      return Filters.USER_FIRST_OR_LAST_NAME;
    default:
      return Filters.NAME;
  }
});

const searchQuery = computed({
  get() {
    return (filters.get(filterKey.value) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(filterKey.value, searchQuery);
  },
});

/**
 * Fetch items
 * @param page
 * @param pageSize
 */
async function fetchItems(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  const params = new Map();

  if (sectors.value?.code && sectors.value?.code !== props.sector.code) {
    setFilter(Filters.SECTORS, [sectors.value.code]);
  }
  setFilter(Filters.SECTORS_EXCLUDED, [props.sector.code]);

  await fetchData(page, pageSize, params);
}

/**
 * Attach item(s) to the sector
 */
async function onAttachToSector() {
  if (attachSelectedItems.value.length > 0) {
    const attachEntitiesPayload: SectorAttachDetachPayload = {
      assign: getItemIds(attachSelectedItems.value),
      unassign: [],
    };
    await attachDetachEntities(
      props.sector.code,
      props.resource,
      attachEntitiesPayload,
    );
    if (errorAttachDetach.value) {
      errorAttachDetach.value.notify(NotificationKey.error);
    } else {
      const responseWithItemDescription = resultsAttachDetach.value.map(
        (action) => ({
          ...action,
          name: getDescription(
            attachSelectedItems.value.find(
              (item) => item[props.primaryKey] === action.name,
            ),
          ),
        }),
      );
      displayActionStatus(
        responseWithItemDescription,
        `${NotificationKey.attachSuccess}.${itemType}`,
        `${NotificationKey.attachError}.${itemType}`,
        props.sector,
        t,
      );
    }

    clearSelectionAndCloseAttachModal();
    emit("change");
  } else {
    showNotificationWarning(t("sectors.modals.attach.errors.list_empty"));
  }
}

function clearSelectionAndCloseAttachModal() {
  toggleAttachModalVisible(false);
  attachSelectedItems.value = [];
}

/**
 * Open Attach modal, fetch items before
 */
async function openAttachModal() {
  toggleAttachModalVisible();
  await fetchItems();
}
</script>

<template>
  <div class="flex" style="justify-content: flex-end">
    <lxc-button
      v-if="displayActions"
      class="self-center"
      html-type="button"
      type="primary"
      :icon="ILxcPlus"
      :title="t(`sectors.details.buttons.attach.${resource}`)"
      @click="openAttachModal"
    >
      {{ t(`sectors.details.buttons.attach.${resource}`) }}
    </lxc-button>
  </div>

  <teleport to="body">
    <lxc-side-canvas
      v-model:show="attachModalVisible"
      :header="t(`sectors.modals.attach.${resource}.title`)"
      class="!min-w-[40%]"
      size="3/4"
      @hidden="clearSelectionAndCloseAttachModal"
    >
      <div>
        <p class="modal-description mt-0">
          {{ t(`sectors.modals.attach.${resource}.description`) }}
        </p>
        <search-bar
          v-model:search-query="searchQuery"
          class="search-bar"
          :search-placeholder="t('label.search')"
          @clear="fetchItems"
          @search="fetchItems"
        />
        <container-component
          :px="0"
          :is-loading="isLoading && !results"
          :error="error"
        >
          <sectors-details-table
            v-model="attachSelectedItems"
            :primary-key="primaryKey"
            :resource="resource"
            :is-loading="isLoading"
            :display-actions="false"
            :row-click-selection-mode="SelectionModeEnum.FORCE"
            :selection="true"
            :items="results"
            :get-description="getDescription"
            :sector="sector"
            @pagination:get-next-page="fetchItems"
          >
            <slot />
          </sectors-details-table>
        </container-component>
      </div>
      <template #footer>
        <div class="flex justify-end gap-6">
          <lxc-button
            class="w-1/6"
            type="secondary"
            html-type="button"
            :title="t('button.cancel')"
            @click="clearSelectionAndCloseAttachModal"
          >
            {{ t("button.cancel") }}
          </lxc-button>

          <lxc-button
            type="primary"
            html-type="button"
            class="w-2/6"
            :title="t('button.confirm')"
            :is-loading="isLoadingAttachDetach"
            @click="onAttachToSector"
          >
            {{ t("button.confirm") }}
          </lxc-button>
        </div>
      </template>
    </lxc-side-canvas>
  </teleport>
</template>
